import {ChangeDetectionStrategy, Component, computed, inject, input, output, signal} from '@angular/core';
import {EmployeeHttpService} from '../../x-http-requests/employee-http.service';
import {
  describeEmployeeIdentificationError
} from '../../../../_shared/errors/employee-identification-errors';

/**
 * Admin control for an employee's <b>employee identification</b> (the PIN critical step-up). It does <i>not</i> set
 * a PIN — only the employee sets their own in self-service. The admin can:
 * <ul>
 *   <li>enable/disable the employee for identification (gates the self-service PIN UI, the device selection list,
 *       and PIN set/verify; disabling also clears any PIN server-side), and</li>
 *   <li>reset (clear) the PIN, forcing the employee to re-enrol — the recovery path for a forgotten PIN.</li>
 * </ul>
 * Self-contained: calls {@link EmployeeHttpService}, surfaces loading / success / error, and emits state changes so
 * the host can keep its employee view in sync. Reuses the documented card / toggle / button / alert patterns.
 */
@Component({
  selector: 'app-employee-identification',
  standalone: true,
  imports: [],
  templateUrl: './employee-identification.component.html',
  styleUrl: './employee-identification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeeIdentificationComponent {
  private readonly employeeHttpService = inject(EmployeeHttpService);

  /** The employee whose identification eligibility is being managed. */
  readonly employeeId = input.required<string>();

  /** Whether the employee is currently enabled for employee identification. */
  readonly enabled = input<boolean>(false);

  /** Whether the employee has enrolled a PIN (drives the reset affordance + status wording). */
  readonly hasPin = input<boolean>(false);

  /** Emitted after a successful enable/disable, so the host can update its model. */
  readonly enabledChange = output<boolean>();

  /** Emitted when the PIN state changes (reset → false; disable clears a PIN → false). */
  readonly hasPinChange = output<boolean>();

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  /** The PIN can only be reset once the employee is enabled and actually has one. */
  readonly canResetPin = computed(() => this.enabled() && this.hasPin() && !this.saving());

  toggle(): void {
    if (this.saving()) return;
    const next = !this.enabled();
    this.saving.set(true);
    this.resetMessages();

    this.employeeHttpService.setEmployeeIdentification(this.employeeId(), next).subscribe({
      next: () => {
        this.saving.set(false);
        this.enabledChange.emit(next);
        // Disabling revokes any enrolled PIN server-side — reflect that in the host model.
        if (!next && this.hasPin()) this.hasPinChange.emit(false);
        this.successMessage.set(next
          ? 'Mitarbeiteridentifikation aktiviert. Der Mitarbeiter vergibt seine PIN im Self-Service.'
          : 'Mitarbeiteridentifikation deaktiviert.');
      },
      error: (err) => {
        this.saving.set(false);
        // German mapped from `ApiError.code`, never `description` (the server's developer-facing English).
        // This endpoint produces no structured code today — see `describeEmployeeIdentificationError`.
        this.errorMessage.set(
          describeEmployeeIdentificationError(err, 'Die Aktion konnte nicht ausgeführt werden.'));
      }
    });
  }

  resetPin(): void {
    if (!this.canResetPin()) return;
    this.saving.set(true);
    this.resetMessages();

    this.employeeHttpService.resetPin(this.employeeId()).subscribe({
      next: () => {
        this.saving.set(false);
        this.hasPinChange.emit(false);
        this.successMessage.set('PIN zurückgesetzt. Der Mitarbeiter vergibt im Self-Service eine neue PIN.');
      },
      error: (err) => {
        this.saving.set(false);
        this.errorMessage.set(
          describeEmployeeIdentificationError(err, 'Die PIN konnte nicht zurückgesetzt werden.'));
      }
    });
  }

  private resetMessages(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }
}
